import React, { useEffect, useRef, useState } from "react";
import { Button } from "@material-ui/core";
import DataTable from "@/components/DataTable";
import { useTranslation } from "@/langs/useTranslation";
import getDevicesControlLogAPI from "@/apis/getDevicesControlLogAPI";
import { formatDate } from "./datetimeHandlers";
import { ReactRender } from "@/components/ReactRender";
import Modal from "@/components/Modal";
import { DeviceControlLogDetailDatatable } from "./DeviceControlLogDetailDatatable";
import { useGlobalState } from "@/GlobalProvider";
import { useUpdateEffect } from "ahooks";
import { useFormik } from "formik";
import * as dayjs from "dayjs";
import useStyles from "@/styles/alarmStyle";
import SearchInput from "@/components/SearchInput";
import { FormItem } from "@/components/Form";
import TagList from "@/components/FormFields/TagList";
import DateRangePicker from "@/components/DateRangePicker";
import TargetType, {
  TargetTypeChipStatus
} from "@/components/FormFields/TargetType";
import CommandStatus from "@/components/FormFields/CommandStatus";
import DeviceGroups from "@/components/FormFields/DeviceGroups/DeviceGroups";
import CollapsePanel, {
  CollapseItem,
  CollapseTrigger
} from "@/components/CollapsePanel";

const TaskType = ({ data, rowData }) => {
  const { t } = useTranslation("device");

  return (
    <div className="task_name">
      {data === "device_power" &&
      rowData.json &&
      rowData.json.system_booting_type ? (
        <>
          {t(`tasks.${data}`)} - [{" "}
          {t(`tasks.${rowData.json.system_booting_type}`)} ]
        </>
      ) : (data === "lock_usb" ||
          data === "disable_combination_key" ||
          data === "disable_windows_notify") &&
        rowData.json ? (
        t(`tasks.${data}_${rowData.json.status}`)
      ) : (
        t(`tasks.${data}`)
      )}
    </div>
  );
};

export const DeviceControlLogsDatatable = ({
  className,
  style,
  footer,
  children
}) => {
  const { t } = useTranslation("deviceControl");
  const tableRef = useRef(null);
  const groupIdRef = useRef(null);
  const [open, setOpen] = useState(false);
  const isReset = useRef(true);
  const { currentCompanyId } = useGlobalState();
  const classes = useStyles();

  const initialValues = {
    target_type: undefined,
    group_ids: undefined,
    tag_ids: undefined,
    status: undefined,
    searching: "",
    date: [null, null]
  };

  const { values, handleChange, handleSubmit, handleReset } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable();
    }
  });

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;
    const date = formValues.date;
    return {
      target_type: formValues.target_type,
      group_ids: formValues.group_ids,
      tag_ids: formValues.tag_ids,
      status: formValues.status,
      searching: formValues.searching,
      start_time: date[0] ? dayjs.utc(date[0]).toISOString() : undefined,
      end_time: date[1] ? dayjs.utc(date[1]).toISOString() : undefined
    };
  };

  const reloadTableWithGroupId = () => {
    isReset.current = false;
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    isReset.current = true;
    tableRef.current?.reload();
  };

  const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = () => {
    handleReset();
    handleReloadDataTable(true);
  };

  const viewDetail = row => {
    groupIdRef.current = row.id;
    setOpen(true);
  };

  useEffect(() => {
    resetQuery();
  }, [currentCompanyId]);

  const columns = [
    {
      data: "task_type",
      title: t({ defaultMessage: "命令" }),
      createdCell: (td, data, rowData) => {
        ReactRender(<TaskType data={data} rowData={rowData} />, td);
      }
    },
    {
      data: "target_type",
      title: t({ defaultMessage: "目标类型" }),
      createdCell: (td, data) => {
        ReactRender(<TargetTypeChipStatus status={data} />, td);
      }
    },
    {
      name: "groups",
      title: t({ defaultMessage: "针对门店" }),
      orderable: false,
      data: row => {
        return row.groups.map(item => item.name).join(", ");
      }
    },
    {
      name: "tags",
      title: t({ defaultMessage: "针对标签" }),
      orderable: false,
      data: row => {
        return row.tags.map(item => item.name).join(", ");
      }
    },
    {
      data: "created_at",
      title: t({ defaultMessage: "发送时间" }),
      render: data => {
        return formatDate(data);
      }
    },
    {
      data: "create_user",
      title: t({ defaultMessage: "使用者" }),
      render: data => (data ? data.name : data)
    },
    {
      data: "total_devices",
      title: t({ defaultMessage: "成功/总数" }),
      createdCell: (td, data, row) => {
        ReactRender(
          <div
            style={{
              backgroundColor:
                row["success_devices"] < row["total_devices"]
                  ? "#ff4038"
                  : "#57cc5b",
              borderRadius: "0.5em 0.5em 0.5em 0.5em",
              color: "white",
              width: "60%",
              textAlign: "center",
              fontWeight: "bold",
              fontFamily: "Helvetica"
            }}
          >
            {`${row["success_devices"]} / ${row["total_devices"]}`}
          </div>,
          td
        );
      }
    },
    {
      data: null,
      title: t({ defaultMessage: "详细内容" }),
      createdCell: (td, data, row) => {
        ReactRender(
          <>
            <Button
              size="small"
              color="primary"
              onClick={() => viewDetail(row)}
            >
              {t({ defaultMessage: "查看详情" })}
            </Button>
          </>,
          td
        );
      }
    }
  ];

  const ajax = (data, callback) => {
    return getDevicesControlLogAPI({
      ...data,
      ...getSearchParams()
    }).then(res => {
      callback({
        data: res.data,
        draw: data.draw,
        recordsFiltered: res.recordsFiltered,
        recordsTotal: res.recordsTotal
      });
    });
  };

  useUpdateEffect(() => {
    tableRef.current?.reload();
  }, [currentCompanyId]);

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className={classes.tableHeader}>
          <CollapsePanel>
            <div className={classes.tableQueryForm}>
              <FormItem
                label={t({ defaultMessage: "目标类型" })}
                className={classes.formItem}
              >
                <TargetType
                  name="target_type"
                  value={values.target_type}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "门店" })}
                className={classes.formItem}
              >
                <DeviceGroups
                  checkStrictly
                  name="group_ids"
                  value={values.group_ids}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "标签" })}
                className={classes.formItem}
              >
                <TagList
                  name="tag_ids"
                  value={values.tag_ids}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "总状态" })}
                className={classes.formItem}
              >
                <CommandStatus
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                />
              </FormItem>
              <CollapseItem>
                <FormItem
                  label={t({ defaultMessage: "关键字" })}
                  className={classes.formItem}
                  style={{ marginRight: 16 }}
                >
                  <SearchInput
                    placeholder={t({ defaultMessage: "搜索" })}
                    name="searching"
                    icon={null}
                    value={values.searching}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "发送时间" })}
                  className={classes.formItem}
                >
                  <DateRangePicker
                    name="date"
                    value={values.date}
                    onChange={handleChange}
                  />
                </FormItem>
              </CollapseItem>
              <FormItem className={classes.formItem}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  style={{ marginRight: 8 }}
                >
                  {t({ defaultMessage: "查询" })}
                </Button>
                <Button variant="outlined" onClick={resetQuery}>
                  {t({ defaultMessage: "重置" })}
                </Button>
                <CollapseTrigger />
              </FormItem>
            </div>
          </CollapsePanel>
        </div>
      </form>
      <DataTable
        ref={tableRef}
        ordering
        defaultOrder={[4, "desc"]}
        columns={columns}
        ajax={ajax}
        className={className}
        style={style}
        footer={footer}
        showCheckAll={false}
      >
        {children}
      </DataTable>
      <Modal
        title={t({ defaultMessage: "命令状态详情" })}
        width={960}
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={() => setOpen(false)}
      >
        <DeviceControlLogDetailDatatable groupId={groupIdRef.current} />
      </Modal>
    </>
  );
};
